import React, { useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { Spin } from "antd";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SelectCorporate from "../components/ui/SelectCorporate";
import SelectRateCard from "../components/ui/SelectRateCard";
import { startOfDay, isBefore, formatISO } from "date-fns";
import SelectPartner from "../components/ui/SelectPartner"; // Import the new component

const SearchInputSection = ({
  checkInDate: propCheckInDate,
  setCheckInDate,
  checkOutDate: propCheckOutDate,
  setCheckOutDate,
  handleSearchResults,
  searchState,
  setSearchState,
  hasSelectedRooms = false,
}) => {
  const [nights, setNights] = useState(1);
  const [bookingSource, setBookingSource] = useState({ value: "0", name: "Walk-In" });
  const [corporateCompany, setCorporateCompany] = useState(null);
  const [marketCode, setMarketCode] = useState("");
  const [reservationType, setReservationType] = useState("confirmed");
  const [selectedRateCard, setSelectedRateCard] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const business_date = localStorage.getItem("business_date");
  const defaultCheckInDate = business_date ? new Date(business_date) : new Date();


  const reservationTypes = [
    { value: "confirmed", label: "Confirm Booking" },
    { value: "tentative", label: "Tentative Booking" },
  ];

  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 1;
    const inDate = startOfDay(new Date(checkIn));
    const outDate = startOfDay(new Date(checkOut));
    const diffDays = Math.ceil((outDate - inDate) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays || 1 : 1;
  };

  const handleDateChange = (type, date) => {

    if (!date) return;
    if (type === "checkIn") {
      setCheckInDate(date);
      let updatedCheckOutDate = propCheckOutDate;
      if (
        propCheckOutDate &&
        isBefore(startOfDay(propCheckOutDate), startOfDay(date))
      ) {
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        updatedCheckOutDate = nextDay;
        setCheckOutDate(nextDay);
      }
      setNights(calculateNights(date, updatedCheckOutDate || propCheckOutDate));
    }
    if (type === "checkOut") {
      setCheckOutDate(date);
      setNights(calculateNights(propCheckInDate || defaultCheckInDate, date));
    }
  };

  const validateForm = () => {
    if (!propCheckInDate) {
      toast.error("Check-In Date is required!");
      return false;
    }
    const checkInStart = startOfDay(propCheckInDate);
    const defaultStart = startOfDay(defaultCheckInDate);

    if (isBefore(checkInStart, defaultStart)) {
      toast.error("Check-In date cannot be in the past.");
      return false;
    }

    if (!propCheckOutDate) {
      toast.error("Check-Out Date is required!");
      return false;
    }

    const checkOutStart = startOfDay(propCheckOutDate);

    if (isBefore(checkOutStart, checkInStart)) {
      toast.error("Check-Out date cannot be before Check-In date.");
      return false;
    }

    if (!bookingSource) {
      toast.error("Booking Source is required!");
      return false;
    }
    if (
      selectedRateCard?.rateCardDetails?.rate_type === "Corporate" &&
      !corporateCompany
    ) {
      toast.error("Corporate Company is required for Corporate Rate Card!");
      return false;
    }
    return true;
  };

  useEffect(() => {
    let initialCheckInDate = propCheckInDate;
    let initialCheckOutDate = propCheckOutDate;

    // Set default check-in date if not set
    if (!initialCheckInDate) {
      initialCheckInDate = defaultCheckInDate;
      setCheckInDate(initialCheckInDate);
    }

    // Set default check-out date only if not set
    if (!initialCheckOutDate) {
      const nextDay = new Date(initialCheckInDate);
      nextDay.setDate(nextDay.getDate() + 1);
      initialCheckOutDate = nextDay;
      setCheckOutDate(initialCheckOutDate);
    }

    // Load stored search params only on mount
    const storedSearchParams = localStorage.getItem("searchParams");
    if (storedSearchParams) {
      const parsedParams = JSON.parse(storedSearchParams);

      localStorage.setItem("bookingsource", parsedParams.bookingSource?.value || "Direct");

      setBookingSource({
        value: parsedParams.bookingSource?.value || "Direct",
        name: parsedParams.bookingSource?.name || "Direct",
      });

      setReservationType(parsedParams.reservationType || "confirmed");
      setMarketCode(parsedParams.marketCode || "");

      if (!selectedRateCard && parsedParams.rateCard) {
        setSelectedRateCard(parsedParams.rateCard);
      }
      if (!corporateCompany && parsedParams.corporateCompany) {
        setCorporateCompany(parsedParams.corporateCompany);
      }
    }

    setNights(calculateNights(initialCheckInDate, initialCheckOutDate));
  }, []); // Empty dependency array to run only on mount

  const handleSelectChange = (key, value) => {
    setSearchState((prev) => {
      const updatedState = { ...prev, [key]: value };
      localStorage.setItem("searchState", JSON.stringify(updatedState));
      return updatedState;
    });
  };

  const handleSearch = async () => {
    if (loading) return;

    // Clear search results, selections, and stored params immediately
    handleSearchResults(null, true);

    const isValid = validateForm();
    if (!isValid) return;

    const searchParams = {
      checkInDate: propCheckInDate,
      checkOutDate: propCheckOutDate,
      nights,
      bookingSource,
      corporateCompany:
        selectedRateCard?.rateCardDetails?.rate_type === "corporate"
          ? corporateCompany
          : null,
      rateCard: selectedRateCard?.value || "0",
      marketCode,
      reservationType,
    };

    setLoading(true);
    try {
      const response = await axios.post(
        `${apiUrl}/booking/room-availability`,
        {
          check_in: formatISO(propCheckInDate, { representation: "complete" }),
          check_out: formatISO(propCheckOutDate, { representation: "complete" }),
          nights,
          booking_source: bookingSource,
          corporate_company:
            selectedRateCard?.rateCardDetails?.rate_type === "corporate"
              ? corporateCompany
              : null,
          rate_card: selectedRateCard?.value || "0",
          market_code: marketCode,
          reservation_type: reservationType,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.setItem("searchParams", JSON.stringify(searchParams));
      localStorage.setItem("searchState", JSON.stringify(searchState));
      handleSearchResults(response.data);
    } catch (error) {
      toast.error("Failed to fetch room availability. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="search-input-section">
      <div className="row">
        <div className="col-md-3 grid-item">
          <label>
            <i className="fas fa-calendar-alt"></i> Check-In
          </label>
          <DatePicker
            selected={propCheckInDate || defaultCheckInDate}
            onChange={(date) => handleDateChange("checkIn", date)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            placeholderText="Select Check-In"
            disabled={hasSelectedRooms}
          />
        </div>
  
        <div className="col-md-3 grid-item">
          <label>
            <i className="fas fa-calendar-check"></i> Check-Out
          </label>
          <DatePicker
            selected={propCheckOutDate}
            onChange={(date) => handleDateChange("checkOut", date)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            placeholderText="Select Check-Out"
            disabled={hasSelectedRooms}
          />
        </div>
  
        <div className="col-md-3 grid-item">
          <label>
            <i className="fas fa-moon"></i> Nights
          </label>
          <div className="nights-display">
            {nights > 0 ? `${nights} Night(s)` : "-"}
          </div>
        </div>
  
        <div className="col-md-3 grid-item">
          <label>
            <i className="fas fa-list"></i> Reservation Type
          </label>
          <Select
            value={reservationTypes.find(
              (option) => option.value === reservationType
            )}
            options={reservationTypes}
            onChange={(selectedOption) =>
              setReservationType(selectedOption?.value || "confirmed")
            }
            isDisabled={hasSelectedRooms}
          />
        </div>
  
        <div className="col-md-3 grid-item">
          <label>
            <i className="fas fa-hotel"></i> Booking Source
          </label>
          <SelectPartner
       
       onSelectPartner={(selectedOption) => {
  const updatedSource = {
    value: selectedOption.value || "Walk-in",
    name: selectedOption.name || "Direct",
  };

  setBookingSource(updatedSource);

  // Update searchState (optional but recommended if you use it elsewhere)
  setSearchState((prev) => {
    const updated = { ...prev, bookingSource: updatedSource };
    localStorage.setItem("searchState", JSON.stringify(updated));
    return updated;
  });

  // Update searchParams too
  const updatedSearchParams = {
    checkInDate: propCheckInDate,
    checkOutDate: propCheckOutDate,
    nights,
    bookingSource: updatedSource,
    corporateCompany:
      selectedRateCard?.rateCardDetails?.rate_type === "corporate"
        ? corporateCompany
        : null,
    rateCard: selectedRateCard?.value || "0",
    marketCode,
    reservationType,
  };
  localStorage.setItem("searchParams", JSON.stringify(updatedSearchParams));

  // Optional: Keep individual field too
  localStorage.setItem("bookingsource", updatedSource.value);
}}


            selectedValue={bookingSource}
            isDisabled={hasSelectedRooms}
          />
        </div>
  
        <div className="col-md-3 grid-item">
  <label>
    <i className="fas fa-tag"></i> Rate Card
  </label>
  <SelectRateCard
    onSelectRateCard={(selectedOption) => {
      setSelectedRateCard(selectedOption);
      if (selectedOption?.rateCardDetails?.rate_type !== "corporate") {
        setCorporateCompany(null);
      }
    }}
    selectedValue={selectedRateCard}
    isDisabled={hasSelectedRooms}
  />
</div>

{selectedRateCard?.rateCardDetails?.rate_type === "corporate" && (
  <div className="col-md-3 grid-item">
    <label>
      <i className="fas fa-building"></i> Corporate Company
    </label>
    <SelectCorporate
      onSelectCompany={(selected) =>
        setCorporateCompany(selected ? selected.value : null)
      }
      selectedValue={corporateCompany}
      isDisabled={hasSelectedRooms}
    />
  </div>
)}
  
        <div className="col-md-3 grid-item">
          <label> &nbsp;<span></span></label>
          <button
          className="btn btn-primary"
          onClick={handleSearch}
          disabled={loading || hasSelectedRooms} // Disable when loading or rooms are selected
        >
          {loading ? <Spin size="small" /> : "Search"}
        </button>
        </div>
      </div>
    </div>
  );
};

export default SearchInputSection;